/**
 * Экран сканирования паспорта (п. 6.2, 6.3 ТЗ).
 *
 * Камера берётся через getUserMedia, а если её нет или доступ не дали —
 * подключается `<input type="file" capture="environment">`. Это не «на всякий
 * случай»: getUserMedia требует HTTPS, и на планшете, открытом по http в
 * локальной сети, доступен только второй путь.
 *
 * Разрешение на камеру запрашивается автоматически при входе на экран, а всё
 * управление доступом вынесено в lib/camera: экран лишь показывает монтажнику
 * одну понятную кнопку под текущее состояние (разрешить / переснять / снять
 * камерой устройства) и сам подхватывает разрешение, если его включили в
 * настройках браузера.
 *
 * Если снимок вышел низкого качества (блик, размытие), экран предлагает
 * пересъёмку ДО перехода к форме с данными — распознанное при этом не теряется,
 * монтажник может продолжить с тем, что есть.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, api } from '../api/client'
import type { Application } from '../api/types'
import {
  type CameraState,
  cameraSupport,
  describeCameraError,
  onCameraPermissionChange,
  requestCameraStream,
} from '../lib/camera'
import { Alert, Button, Steps } from '../components/ui'
import { qualityLabel } from '../lib/format'

const JPEG_QUALITY = 0.9
// Больше 2000px по длинной стороне не нужно: пайплайн всё равно уменьшает
// изображение до 1500px, а на медленной связи каждый лишний мегабайт — минуты.
const MAX_DIMENSION = 2000

interface Props {
  application: Application
  onDone: (application: Application) => void
  onBack: () => void
}

type Phase = 'camera' | 'preview' | 'uploading' | 'quality-warning'

export function ScanScreen({ application, onDone, onBack }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  // Свежая фаза для слушателя разрешений — без переподписки на каждый шаг.
  const phaseRef = useRef<Phase>('camera')

  const [phase, setPhase] = useState<Phase>('camera')
  // Состояние доступа к камере и текст под него. 'granted' пока поток не
  // получен трактуем как «идёт запрос»; финальные состояния ставит startCamera.
  const [cameraState, setCameraState] = useState<CameraState>(() =>
    cameraSupport().hasGetUserMedia ? 'prompt' : 'insecure',
  )
  const [cameraMessage, setCameraMessage] = useState<string | null>(null)
  const [streaming, setStreaming] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [scanned, setScanned] = useState<Application | null>(null)
  const [qualityIssues, setQualityIssues] = useState<string[]>([])

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    setStreaming(false)
  }, [])

  // Запрос доступа и запуск потока. Вызывается автоматически при входе на экран
  // и по кнопке «Разрешить камеру». Разбор ошибок и состояний — в lib/camera.
  const startCamera = useCallback(async () => {
    setCameraMessage(null)
    const { hasGetUserMedia } = cameraSupport()
    if (!hasGetUserMedia) {
      // http:// в локальной сети: живой камеры не будет, но это не поломка —
      // сразу ведём к съёмке камерой устройства, без тревожного тона.
      setCameraState(cameraSupport().secure ? 'unsupported' : 'insecure')
      return
    }
    try {
      const stream = await requestCameraStream()
      streamRef.current = stream
      setCameraState('granted')
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => undefined)
      }
      setStreaming(true)
    } catch (err) {
      const failure = describeCameraError(err)
      setCameraState(failure.state)
      setCameraMessage(failure.message)
      setStreaming(false)
    }
  }, [])

  useEffect(() => {
    void startCamera()
    return stopCamera
  }, [startCamera, stopCamera])

  // Авто-восстановление: монтажник включил камеру в настройках браузера —
  // подхватываем разрешение и запускаем поток сами, пока он на экране камеры.
  useEffect(() => {
    return onCameraPermissionChange((state) => {
      if (state === 'granted' && phaseRef.current === 'camera' && !streamRef.current) {
        void startCamera()
      } else if (state === 'denied') {
        stopCamera()
        setCameraState('denied')
      }
    })
  }, [startCamera, stopCamera])

  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  useEffect(() => {
    // URL превью нужно освобождать, иначе на десятке пересъёмок память течёт.
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  function showPreview(next: Blob) {
    if (preview) URL.revokeObjectURL(preview)
    setBlob(next)
    setPreview(URL.createObjectURL(next))
    setPhase('preview')
    stopCamera()
  }

  const capture = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return

    const scale = Math.min(1, MAX_DIMENSION / Math.max(video.videoWidth, video.videoHeight))
    const canvas = (canvasRef.current ??= document.createElement('canvas'))
    canvas.width = Math.round(video.videoWidth * scale)
    canvas.height = Math.round(video.videoHeight * scale)
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(
      (result) => {
        if (result) showPreview(result)
      },
      'image/jpeg',
      JPEG_QUALITY,
    )
  }

  const pickFile = (files: FileList | null) => {
    const file = files?.[0]
    if (file) showPreview(file)
  }

  const retake = () => {
    setError(null)
    setScanned(null)
    setQualityIssues([])
    setBlob(null)
    if (preview) URL.revokeObjectURL(preview)
    setPreview(null)
    setPhase('camera')
    void startCamera()
  }

  const upload = async () => {
    if (!blob) return
    setError(null)
    setPhase('uploading')
    try {
      const updated = await api.scanPassport(application.id, blob)
      const meta = updated.ocr_meta
      if (meta?.low_quality && meta.quality_issues?.length) {
        // Данные уже сохранены — предлагаем переснять, но не заставляем.
        setScanned(updated)
        setQualityIssues(meta.quality_issues)
        setPhase('quality-warning')
        return
      }
      onDone(updated)
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка'))
      setPhase('preview')
    }
  }

  // Живая камера в браузере доступна только с полученным потоком. Если контекст
  // не защищён (http://) или камеры нет — единственный путь съёмка устройством.
  const liveCamera = cameraState === 'granted' && streaming
  const deviceCameraOnly = cameraState === 'insecure' || cameraState === 'unsupported'

  return (
    <>
      <Steps current={1} />
      <div className="card">
        <h2>Снимок паспорта</h2>
        <p className="subtitle">
          Сфотографируйте разворот с фотографией. Документ — полностью в рамке, без блика.
          Снимок нужен только для распознавания и нигде не сохраняется.
        </p>

        {error && (
          <Alert
            kind="error"
            title={error.retryable ? 'Не удалось отправить снимок' : 'Снимок не принят'}
            onRetry={error.retryable ? upload : undefined}
            onDismiss={() => setError(null)}
          >
            {error.message}
          </Alert>
        )}

        {phase === 'quality-warning' && (
          <Alert
            kind="warn"
            title="Низкое качество снимка"
            onRetry={retake}
            retryLabel="Переснять"
          >
            Обнаружено: {qualityIssues.map(qualityLabel).join(', ')}. Часть полей могла
            распознаться неверно — их видно на следующем экране. Можно переснять или
            продолжить и проверить данные вручную.
          </Alert>
        )}

        {phase === 'camera' && cameraState === 'denied' && (
          <Alert
            kind="warn"
            title="Нужен доступ к камере"
            onRetry={() => void startCamera()}
            retryLabel="Разрешить камеру"
          >
            {cameraMessage ??
              'Доступ к камере запрещён. Нажмите «Разрешить камеру» или снимите через камеру устройства.'}
          </Alert>
        )}

        {phase === 'camera' && deviceCameraOnly && (
          <Alert kind="warn" title="Снимок — камерой устройства">
            {cameraMessage ??
              'Здесь камера открывается прямо из устройства. Нажмите «Открыть камеру» ниже, ' +
                'сфотографируйте разворот паспорта — и снимок попадёт в форму.'}
          </Alert>
        )}

        <div className="camera">
          {phase === 'camera' ? (
            <>
              <video ref={videoRef} playsInline muted autoPlay />
              {liveCamera ? (
                <div className="frame" />
              ) : (
                <div className="placeholder">
                  {cameraState === 'denied'
                    ? 'Разрешите доступ к камере или нажмите «Открыть камеру» ниже'
                    : 'Нажмите «Открыть камеру» — откроется камера устройства'}
                </div>
              )}
            </>
          ) : (
            preview && <img src={preview} alt="Снимок паспорта" />
          )}
        </div>

        {phase === 'camera' && (
          <div className="actions">
            <Button variant="secondary" onClick={onBack}>
              Назад
            </Button>
            <label className={deviceCameraOnly ? 'btn' : 'btn secondary'}>
              {deviceCameraOnly ? 'Открыть камеру' : 'Снять через файл'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(event) => pickFile(event.target.files)}
              />
            </label>
            {liveCamera && <Button onClick={capture}>Сделать снимок</Button>}
          </div>
        )}

        {(phase === 'preview' || phase === 'uploading') && (
          <div className="actions">
            <Button variant="secondary" onClick={retake} disabled={phase === 'uploading'}>
              Переснять
            </Button>
            <Button onClick={upload} busy={phase === 'uploading'}>
              {phase === 'uploading' ? 'Распознаём…' : 'Распознать'}
            </Button>
          </div>
        )}

        {phase === 'quality-warning' && scanned && (
          <div className="actions">
            <Button variant="secondary" onClick={retake}>
              Переснять
            </Button>
            <Button onClick={() => onDone(scanned)}>Продолжить</Button>
          </div>
        )}

        {phase === 'uploading' && (
          <p className="subtitle" style={{ marginTop: 12, marginBottom: 0 }}>
            Распознавание идёт на сервере и занимает несколько секунд. Не закрывайте экран.
          </p>
        )}
      </div>
    </>
  )
}
